import React, { useState, useEffect, useCallback } from "react";
import { getUserPermissions, getOwnDetails, getApplications, getAssessmentsForUser, getBranding } from "../utils/api";

const DashboardContext = React.createContext();

function DashboardProvider(props) {
    const [loggedIn, setLoggedIn] = useState(false);
    const [permissionsLoading, setPermissionsLoading] = useState(true);
    const [userPermissions, setUserPermissions] = useState([]);
    const [userDetails, setUserDetails] = useState([]);
    const [applicationsLoading, setApplicationsLoading] = useState(true);
    const [userApplications, setUserApplications] = useState([]);
    const [userAssessments, setUserAssessments] = useState([]);
    const [assessmentsLoading, setAssessmentsLoading] = useState(true);
    const [branding, setBranding] = useState({});
    const [brandingLoading, setBrandingLoading] = useState(true);
    const [home, setHome] = useState(null);

    const fetchUserPermissions = async () => {
        setPermissionsLoading(true);
        const userPermissionsResponse = await getUserPermissions();
        setUserPermissions(userPermissionsResponse.map((u) => u.code));
        setPermissionsLoading(false);
    };

    const fetchUserDetails = async () => {
        const userDetailsResponse = await getOwnDetails();
        setUserDetails({
            name: userDetailsResponse.name,
            email: userDetailsResponse.emailAddress,
            organisationName: userDetailsResponse.organisationName,
        });
    };

    const fetchAssessments = async () => {
        setAssessmentsLoading(true);
        try {
            const userAssessmentsResponse = await getAssessmentsForUser();
            setUserAssessments(userAssessmentsResponse);
        } catch {
            setUserAssessments(null);
        }
        setAssessmentsLoading(false);
    };

    const fetchApps = async () => {
        setApplicationsLoading(true);
        const userApplicationsResponse = await getApplications();
        setUserApplications(userApplicationsResponse);
        setApplicationsLoading(false);
    };

    const fetchBranding = async () => {
        setBrandingLoading(true);
        const brandingResponse = await getBranding(window.location.hostname);
        const brandingDict = brandingResponse.reduce((obj, item) => Object.assign(obj, { [item.key]: item.value }), {});
        setBranding(brandingDict);

        // Set client theme if available
        // if not, PP theme is set where primary and secondary are defaulted to in tailwind.config.js
        if (brandingDict.clientTheme != null) {
            const style = document.createElement("style");
            style.innerHTML = brandingDict.clientTheme;
            document.head.appendChild(style);
        }

        setBrandingLoading(false);
    };

    const getImage = (key) => {
        return brandingLoading || !branding[key] ? "" : `data:image/png;base64,${branding[key]}`;
    };

    useEffect(() => {
        fetchBranding();
    }, []);

    useEffect(() => {
        if (loggedIn) {
            fetchUserDetails();
            fetchApps();
            fetchAssessments();
            fetchUserPermissions();
        }
    }, [loggedIn]);

    const determineHome = useCallback(
        (permissions) => {
            if (
                permissions.includes("CP-A-ASSESS") &&
                userAssessments &&
                userAssessments.some((assessment) => !assessment.isCompleted)
            ) {
                return "/assessments";
            } else if (
                (permissions.includes("CP-A-APPS") ||
                    permissions.includes("CP-A-USRMGR") ||
                    permissions.includes("CP-A-GRPMGR")) &&
                userApplications.length > 0
            ) {
                return "/apps";
            } else if (permissions.includes("CP-A-REPORTS")) {
                return "/reports";
            } else if (permissions.includes("CP-A-ASSESS")) {
                return "/assessments";
            } else if (permissions.includes("CP-A-APPS")) {
                return "/apps";
            } else if (permissions.includes("CP-A-HLPSPT")) {
                return "/help-and-support";
            } else {
                return "/welcome";
            }
        },
        [userApplications.length, userAssessments]
    );

    useEffect(() => {
        if (!permissionsLoading && !assessmentsLoading && !assessmentsLoading) {
            setHome(determineHome(userPermissions));
        }
    }, [permissionsLoading, applicationsLoading, assessmentsLoading, determineHome, userPermissions]);

    return (
        <DashboardContext.Provider
            value={{
                loggedIn: loggedIn,
                setLoggedIn: setLoggedIn,
                permissionsLoading: permissionsLoading,
                userPermissions: userPermissions,
                userDetails: userDetails,
                applicationsLoading: applicationsLoading,
                userApplications: userApplications,
                userAssessments: userAssessments,
                assessmentsLoading: assessmentsLoading,
                fetchAssessments: fetchAssessments,
                branding: branding,
                brandingLoading: brandingLoading,
                home: home,
                getImage: getImage,
            }}
        >
            {props.children}
        </DashboardContext.Provider>
    );
}

export { DashboardProvider, DashboardContext };
